import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { vacationOrder, vacationCalculation, type VacationCalculationPreview } from '@/api/vacation';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import InfoQ from '@/shared/components/InfoQ';

const fmt2 = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err?.response?.data?.detail ?? err?.message ?? 'Помилка';
}

export default function VacationCalculationCreatePage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [preview, setPreview] = useState<VacationCalculationPreview | null>(null);
  const [avgOverride, setAvgOverride] = useState('');
  const [error, setError] = useState<string | null>(null);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Накази відпустки обраної org (оплачувані — UNPAID не нараховує).
  const ordersQ = useQuery({
    queryKey: ['vacation-orders', orgId],
    queryFn: () => vacationOrder.list({ organization_id: orgId, limit: 500 }),
    enabled: !!orgId,
  });
  // Вже створені розрахунки (щоб не пропонувати наказ, де розрахунок вже є).
  const calcsQ = useQuery({
    queryKey: ['vacation-calculations', orgId],
    queryFn: () => vacationCalculation.list({ organization_id: orgId, limit: 500 }),
    enabled: !!orgId,
  });
  const empQ = useQuery({
    queryKey: ['employees', orgId],
    queryFn: () => refs.employees(orgId, {}),
    enabled: !!orgId,
  });

  const usedOrderIds = useMemo(
    () => new Set((calcsQ.data?.items ?? []).map((c) => c.vacation_order_id)),
    [calcsQ.data],
  );
  const availableOrders = useMemo(
    () => (ordersQ.data?.items ?? []).filter(
      (o) => o.is_paid && !o.deletion_mark && !usedOrderIds.has(o.id)),
    [ordersQ.data, usedOrderIds],
  );
  const order = availableOrders.find((o) => o.id === orderId);
  const empName = empQ.data?.find((e) => e.id === order?.employee_id)?.name ?? '';

  const previewMut = useMutation({
    mutationFn: () => vacationCalculation.preview({
      organization_id: orgId, vacation_order_id: orderId,
      average_daily_override: avgOverride ? avgOverride : null,
    }),
    onSuccess: (r) => {
      setPreview(r); setError(null);
      // Підставити пораховану середню (щоб бухгалтер бачив і міг перебити).
      if (!avgOverride) setAvgOverride(trimNum(r.average_daily_wage));
    },
    onError: (e) => setError(extractError(e)),
  });
  const saveMut = useMutation({
    mutationFn: () => vacationCalculation.createDraft({
      organization_id: orgId, vacation_order_id: orderId,
      average_daily_override: avgOverride ? avgOverride : null,
    }),
    onSuccess: (doc) => {
      setError(null);
      if (doc?.id) navigate(`/hr/vacation-calculations/${doc.id}`);
      else navigate('/payrolls');
    },
    onError: (e) => setError(extractError(e)),
  });

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нове нарахування (відпускні)</h2>
        <button style={{ marginLeft: 'auto' }} onClick={() => navigate('/payrolls')}>← До списку</button>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Нарахування відпускних — на основі наказу відпустки. Оберіть організацію і наказ —
        розрахунок підтягнеться автоматично.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <div className="field" style={{ minWidth: 220, margin: 0 }}>
          <label>Організація *</label>
          <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setOrderId(''); setPreview(null); }}>
            <option value="">— оберіть —</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 320, margin: 0 }}>
          <label>Наказ відпустки *</label>
          <select value={orderId} disabled={!orgId}
            onChange={(e) => { setOrderId(e.target.value); setPreview(null); }}>
            <option value="">— оберіть наказ —</option>
            {availableOrders.map((o) => {
              const nm = empQ.data?.find((e) => e.id === o.employee_id)?.name ?? o.employee_id;
              return (
                <option key={o.id} value={o.id}>
                  {o.document_number} — {nm} ({o.vacation_start_date} → {o.vacation_end_date})
                </option>
              );
            })}
          </select>
        </div>
        <div className="field" style={{ width: 160, margin: 0 }}>
          <label>Середньоденна</label>
          <input type="number" step="0.01" value={avgOverride} disabled={!orderId}
            placeholder="авто з заробітку"
            onChange={(e) => { setAvgOverride(e.target.value); setPreview(null); }} />
        </div>
        <button className="primary" disabled={!orderId || previewMut.isPending}
          onClick={() => previewMut.mutate()}>
          🧮 Розрахувати
        </button>
      </div>
      {orgId && availableOrders.length === 0 && !ordersQ.isLoading && (
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Немає оплачуваних наказів відпустки без нарахування. Спершу створіть наказ відпустки.
        </p>
      )}

      {error && <div className="error">{error}</div>}

      {preview && (
        <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
                <th style={{ textAlign: 'left', padding: '0.2rem 0.3rem' }}>Працівник</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 70 }}>Днів</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Середня</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Брутто</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ПДФО</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ВЗ</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ЄСВ</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 100 }}>До виплати</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.15rem 0.3rem' }}>{empName}</td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{preview.days_count}</td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{fmt2(preview.average_daily_wage)}</td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{fmt2(preview.vacation_gross_amount)}</td>
                <td style={{ padding: '0.15rem 0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.pdfo_amount)}
                      style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                    <InfoQ title={`ПДФО — ${trimNum(preview.pdfo_rate)}% від брутто.`} />
                  </div>
                </td>
                <td style={{ padding: '0.15rem 0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.wz_amount)}
                      style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                    <InfoQ title={`Військовий збір — ${trimNum(preview.wz_rate)}% від брутто.`} />
                  </div>
                </td>
                <td style={{ padding: '0.15rem 0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.esv_amount)}
                      style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                    <InfoQ title={esvTipDoc(preview.esv_amount, preview.esv_rate)} />
                  </div>
                </td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{fmt2(preview.net_amount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '0.75rem' }}>
        <button className="primary" disabled={!orderId || saveMut.isPending}
          title={!orderId ? 'Спершу оберіть наказ відпустки' : 'Створити чернетку нарахування'}
          onClick={() => saveMut.mutate()}>
          💾 Зберегти нарахування
        </button>
      </div>
    </div>
  );
}
